import { toNodeNumberCell } from '../stores/utils/node-number';
import { isEqual } from 'lodash';
import type {
  JsonValue,
  NodeUpdate,
  Placement,
  ProStudioNode,
} from '@pypestream/pro-studio-collab-client';

import type { TableRow } from '../stores/table-store';

/**
 * The write half of the PSTUDIO-4450 projection (PSTUDIO-4451): turns a table
 * row back into the collab document's node fields.
 *
 * The table keeps writing full rows through its own save funnels; under live
 * editing those rows are translated here into the smallest store mutation — a
 * patch of only the fields that differ from the document, a new node, or a
 * placement between neighbours. What is the table's own and never a write:
 * `order` (the document orders by position), `hidden`, and the group header's
 * derived `subRows` and per-user `groupExpanded`.
 *
 * Type-only imports from the collab client, as in the projection.
 */
type NodeFields = Omit<ProStudioNode, 'id' | 'order'>;

const str = (value: unknown): string | null =>
  value === null || value === undefined ? null : String(value);

const strList = (value: unknown): string[] =>
  Array.isArray(value) ? value.map((v) => String(v)) : [];

const json = (value: unknown): JsonValue | null =>
  value === undefined ? null : (value as JsonValue | null);

/** The node fields a table row stands for, normalised to the document's types. */
const toNodeFields = (row: TableRow): NodeFields => {
  const r = row as unknown as Record<string, unknown>;
  const settings = (row.settings ?? {}) as Record<string, unknown>;
  const isGroup = String(r.nodeType ?? '').toLowerCase() === 'g';

  return {
    nodeNumber: toNodeNumberCell(r.nodeNumber),
    nodeType: String(r.nodeType ?? 'd'),
    // PSTUDIO-4451: a task is a node the table marks with `settings.taskId`.
    isTask: typeof settings.taskId === 'string' && settings.taskId !== '',
    groupId: str(settings.groupId),
    groupName: isGroup ? str(settings.groupName) : null,
    groupColor: isGroup ? str(settings.groupColor) : null,
    nodeName: str(r.nodeName),
    intent: str(r.intent),
    description: str(r.description),
    message: str(r.message),
    richAssetType: String(r.richAssetType ?? ''),
    richAssetContent: json(r.richAssetContent),
    answerRequired: r.answerRequired === true,
    nluDisabled: r.nluDisabled === true,
    behaviors: strList(r.behaviors),
    cssClassName: str(r.cssClassname),
    nextNodes: strList(r.nextNodes),
    command: str(r.command),
    output: strList(r.output),
    nodeInput: str(r.nodeInput),
    parameterInput: json(r.parameterInput),
    decisionVariable: str(r.decisionVariable),
    whatsNext: strList(r.whatsNext),
    nodeTags: str(r.nodeTags),
    skillTag: str(r.skillTag),
    variables: strList(r.variables),
    platformFlag: str(r.platformFlag),
    utterances: str(r.utterances),
    analyticsEvent: json(r.analyticsEvent),
    isSupervisorConfig:
      typeof settings.shouldSyncWithSupervisorConfig === 'boolean'
        ? settings.shouldSyncWithSupervisorConfig
        : null,
  };
};

// `isSupervisorConfig` only ever comes from a row that carries it.
const SKIP_WHEN_ABSENT = new Set<keyof NodeFields>(['isSupervisorConfig']);

/**
 * The projection shows a node number in canonical form (PSTUDIO-4453), so the
 * document's own spelling of the same number — `'01'`, `'1.0'` — comes back as
 * `'1'`. A row that did not change the number must not rewrite it: compare
 * canonical forms.
 */
const sameNumber = (next: string | null, current: string | null) =>
  toNodeNumberCell(next) === toNodeNumberCell(current);
const sameRefs = (next: string[], current: string[]) =>
  next.length === current.length &&
  next.every((ref, i) => {
    const shown = Number(current[i]);
    return (
      ref === current[i] || (Number.isFinite(shown) && String(shown) === ref)
    );
  });

/** Only the fields of `row` that differ from the document's `current` node. */
export const toNodePatch = (
  row: TableRow,
  current: ProStudioNode
): NodeUpdate['patch'] => {
  const next = toNodeFields(row);
  const patch: Record<string, unknown> = {};
  for (const key of Object.keys(next) as (keyof NodeFields)[]) {
    if (SKIP_WHEN_ABSENT.has(key) && next[key] === null) continue;
    if (
      key === 'nodeType' &&
      next.nodeType.toLowerCase() === current.nodeType.toLowerCase()
    ) {
      continue;
    }
    if (
      key === 'nodeNumber' &&
      sameNumber(next.nodeNumber, current.nodeNumber)
    ) {
      continue;
    }
    if (key === 'nextNodes' && sameRefs(next.nextNodes, current.nextNodes)) {
      continue;
    }
    if (!isEqual(next[key], current[key])) patch[key] = next[key];
  }
  return patch as NodeUpdate['patch'];
};

/** A whole node for a row the document does not have yet. Its order is set by placement. */
export const toNewNode = (row: TableRow): ProStudioNode => ({
  id: row.id,
  order: '',
  ...toNodeFields(row),
});

/** Where `id` sits among `sorted`: after the row before it, or before the next one. */
export const placementFor = (
  id: string,
  sorted: readonly { id: string }[]
): Placement => {
  const index = sorted.findIndex((r) => r.id === id);
  if (index === -1) {
    const last = sorted[sorted.length - 1];
    return last ? { after: last.id } : {};
  }
  if (index > 0) return { after: sorted[index - 1].id };
  const next = sorted[1];
  return next ? { before: next.id } : {};
};
